import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RoutingStateService } from '@brightside/brightside-ui-services';
import {
  CreditSetupResponse,
  CreditSetupKbaQuiz,
  CreditSetupKbaQuizQuestion,
  CreditSetupKbaQuestionAnswer,
  CreditSetupVerifyResponse,
  CreditSetupVerifyStatus,
  isTemplateFullScreen,
  FirebaseService,
  MobileStateService,
} from '@brightside-web/desktop/data-access/shared';
import { CreditSetupService } from '@brightside-web/desktop/data-access/credit';

import { BsCacheService, BsChatService } from '@brightside-web/desktop/data-access/core-services';

import { Subscription } from 'rxjs';
import { Intercom } from '@supy-io/ngx-intercom';
import { AbstractCreditSetupComponent } from '../abstract-credit-setup.component';
import { Environment } from '@brightside-web/micro/core/environment';

@Component({
    selector: 'brightside-web-verify-kba',
    templateUrl: './verify-kba.component.html',
    styleUrls: ['./verify-kba.component.scss'],
    standalone: false
})
export class VerifyKbaComponent extends AbstractCreditSetupComponent implements OnInit, OnDestroy {
  private maxKbaRetryAttempts = 1;

  private retryAttempts = 0;
  private failedAttempts = 0;

  isLoadingQuestions = true;

  showErrorModal = false;
  showErrorNoRetryModal = false;

  questionForm: FormGroup = this.formBuilder.group({});

  currentQuestionIndex = 0;
  currentQuestionAnswers: CreditSetupKbaQuestionAnswer[] = [];

  currentQuiz: CreditSetupKbaQuiz;

  creditKbaOnboardingEnabled = this.analytics.getValueAsBoolean('creditKbaOnboardingEnabled');

  constructor(
    protected router: Router,
    protected routingState: RoutingStateService,
    protected activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private intercom: Intercom,
    private creditSetupService: CreditSetupService,
    protected env: Environment,
    protected analytics: FirebaseService,
    private mobileStateService: MobileStateService,
    public bsCacheService: BsCacheService,
    private _bsChatService: BsChatService
  ) {
    super(router, routingState, activatedRoute, env, analytics);
  }

  ngOnInit() {
    super.ngOnInit();
    this.sub.add(
      this.mobileStateService.get().subscribe((ms) => {
        if (ms.onboardingComplete || this.creditKbaOnboardingEnabled) {
          this.subscribeAndGetStatus();
          this.analytics.logEvent('credit_kba_started', { type: 'first time' });
        }
      })
    );
  }

  get areQuestionsLoading(): boolean {
    return this.isLoadingQuestions && !this.currentQuestion.choices;
  }

  get currentQuestion(): CreditSetupKbaQuizQuestion {
    return (this.currentQuiz && this.currentQuiz.questions[this.currentQuestionIndex]) || {};
  }

  private resetQuizData() {
    this.isLoadingQuestions = true;

    this.currentQuestionIndex = 0;
    this.currentQuestionAnswers = [];
  }

  private resetModals() {
    this.showErrorModal = false;
    this.showErrorNoRetryModal = false;
  }

  private subscribeAndGetStatus() {
    const handleSuccess = (resp: CreditSetupResponse) => {
      this.failedAttempts = resp.kbaFailedAttempts || 0;

      this.subscribeAndStartQuiz();
    };
    const handleFailure = () => {
      this.isLoadingQuestions = false;
      //TODO add credit failed here, need to see if onboarding or standard flow
      this.analytics.logEvent('error_shown', { 'error id': 'credit_setup_failed' });
      this.toggleErrorModal();
    };

    this.sub.add(this.creditSetupService.getSetupStatus().subscribe(handleSuccess, handleFailure));
  }

  private subscribeAndStartQuiz() {
    if (this.failedAttempts > 0) {
      this.analytics.logEvent('credit_kba_started', { type: 'retry' });
    }
    const handleRetry = () => {
      if (this.failedAttempts < 1) {
        this.analytics.logEvent('credit_kba_failed', { type: 'first time' });
      } else {
        this.analytics.logEvent('credit_kba_failed', { type: 'retry' });
      }

      if (this.retryAttempts < 3) {
        this.retryAttempts += 1;

        this.resetQuizData();
        setTimeout(() => {
          this.subscribeAndGetStatus();
        }, 1500);
      } else {
        handleFailure();
      }
    };
    const handleSuccess = (resp: CreditSetupResponse) => {
      if (!resp.kbaQuiz) {
        handleRetry();
        return;
      }

      this.showErrorModal = false;
      this.isLoadingQuestions = false;

      this.currentQuiz = resp.kbaQuiz;
      this.questionForm = this.formBuilder.group({
        questionAnswer: ['', [Validators.required]],
      });
    };
    const handleFailure = () => {
      this.isLoadingQuestions = false;
      this.analytics.logEvent('error_shown', { 'error id': 'credit_setup_no_kba' });
      this.toggleErrorModal();
    };

    this.resetQuizData();
    this.sub.add(this.creditSetupService.startSetup().subscribe(handleSuccess, handleRetry));
  }

  private displayErrorModal() {
    this.processing = false;
    this.analytics.logEvent('error_shown', { 'error id': 'credit_setup_kba_submit' });
    this.toggleErrorModal();
  }

  logV4(event_name: string) {
    if (event_name) {
      console.log(`FIREBASE v4: ${event_name}`);
      this.analytics.logEvent(event_name);
    }
  }

  private async submitKbaAnswers() {
    const handleResponse = async (resp: CreditSetupVerifyResponse) => {
      switch (resp.verifyStatus) {
        case CreditSetupVerifyStatus.COMPLETE:
          this.logV4('credit_kba_completed');
          this.handleRouteForward(true, await this.completeRoute());
          break;

        case CreditSetupVerifyStatus.COMPLETE_UNCONFIRMED:
          this.logV4('credit_kba_completed');
          this.handleRouteForward(true, await this.completeRoute());
          break;

        case CreditSetupVerifyStatus.MORE_INFORMATION:
          this.resetQuizData();

          this.currentQuiz = resp.apiResponse?.kbaQuiz || this.currentQuiz;
          this.isLoadingQuestions = false;
          this.processing = false;
          break;

        case CreditSetupVerifyStatus.ERROR_NO_RETRY:
          this.failedAttempts = 99;
          this.displayErrorModal();
          break;
        default:
          this.displayErrorModal();
          break;
      }
    };

    this.processing = true;

    this.sub.add(
      this.creditSetupService
        .VerifyWithKba({
          type: 'kbaQuiz',
          answers: this.currentQuestionAnswers,
        })
        .subscribe(handleResponse, handleResponse)
    );
  }

  private toggleErrorModal() {
    this.resetModals();

    if (this.failedAttempts >= this.maxKbaRetryAttempts) {
      this.logV4('credit_kba_non_retry_shown');
      this.showErrorNoRetryModal = true;
    } else {
      this.logV4('credit_kba_retry_shown');
      this.showErrorModal = true;
    }
  }

  handleCtaClick() {
    const questionAnswerFormItem = this.questionForm.get('questionAnswer');

    if (questionAnswerFormItem) {
      //Push the selected answer up
      this.currentQuestionAnswers.push({
        questionId: +this.currentQuestion.id,
        answerId: +questionAnswerFormItem.value,
      });
      questionAnswerFormItem.setValue('');
    }

    // Check if we got to last question
    if (!this.currentQuiz.questions[this.currentQuestionIndex + 1]) {
      this.submitKbaAnswers();
      return;
    }

    // Log answer response and move to next question
    this.currentQuestionIndex += 1;
  }

  handleRetryCtaClick() {
    this.subscribeAndGetStatus();
  }

  handleCancelCtaClick(inRetryFlow: boolean = false, showIntercom: boolean = false) {
    if (inRetryFlow) {
      this.logV4('credit_kba_retry_skipped');
    }
    this.bsCacheService.setItem('creditFailed', true);

    this.handleRouteForward(true, ['home']);

    if (showIntercom) {
      this._bsChatService.showChat();
      this.analytics.logEvent('chat_shown', { source: 'auto' }, true);
    }

    return false;
  }
}
